import React, { useContext, useEffect, useState } from 'react'
import AuthContext from '../../context/AuthContext';
import DrawerContext from '../../context/DrawerContext';
import CustomButton from '../../common/CustomButton';
import PageSearchBar from '../../common/PageSearchBar';
import './PricingTable.scss';

//MUI
import { useTheme } from '@emotion/react';
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage, MoreVertRounded, EditRounded } from '@mui/icons-material';
import { CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Select, FormControl } from '@mui/material';
import { Box } from '@mui/system';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}

// Helper function to format numbers: show whole number if integer, otherwise show actual decimal value
const formatNumber = (num) => {
  if (num === null || num === undefined) return '';
  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(numValue)) return '';
  
  // Check if it's a whole number (no decimal part)
  if (numValue % 1 === 0) {
    return Math.floor(numValue).toString();
  }
  
  // Otherwise, show the actual decimal value
  // Convert to string and remove trailing zeros after decimal point
  return numValue.toString().replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

// Format period for display: use singular (e.g. "1 day", "1 month") when amount is 1; API returns plural
const formatPeriodDisplay = (amount, units) => {
  if (amount == null || units == null) return '';
  const num = typeof amount === 'string' ? parseInt(amount, 10) : amount;
  if (num === 1 && typeof units === 'string') {
    const singular = units.replace(/s$/, '');
    return `${amount} ${singular}`;
  }
  return `${amount} ${units}`;
};

export default function PricingTable() {
  const drawerCtx = useContext(DrawerContext);
  const authCtx = useContext(AuthContext);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [selectedRates, setSelectedRates] = useState(null);
  const [ratesData, setRatesData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('ZA'); // Default to South Africa
  const [pointsConversion, setPointsConversion] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [displayedData, setDisplayedData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [updatingRows, setUpdatingRows] = useState(new Set());

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - displayedData.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getAllRates = (countryCode = 'ZA') => {
    setLoading(true);
    const url = `${process.env.REACT_APP_API_URI}/v3/rates?country=${countryCode}`;
    
    fetch(url, {
      method: 'GET',
      headers: { 
        'Content-Type': 'application/json', 
        'Authorization': `Bearer ${authCtx.token}`
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === 'success') {
        // Handle new API response structure
        const responseData = data.data || {};
        setRatesData(responseData.rates || []);
        setCountries(responseData.countries || []);
        
        // Update points conversion and currency for selected country
        if (responseData.countries) {
          const selectedCountryData = responseData.countries.find(c => c.iso === countryCode);
          if (selectedCountryData) {
            setPointsConversion(selectedCountryData.pointsConversion);
            setCurrency(selectedCountryData.currency);
          }
        }
      }
      setLoading(false);
    })
    .catch(err => {
      console.error(err);
      setLoading(false);
    });
  }

  useEffect(() => {
    getAllRates('ZA'); // Load South Africa by default
  }, []);

  const handleCountryChange = (event) => {
    const countryCode = event.target.value;
    setSelectedCountry(countryCode);
    setPage(0); // Reset to first page when country changes
    getAllRates(countryCode);
  };

  useEffect(() => {
    const filteredData = ratesData.filter((data) => {
      const periodText = `${data.amount || ''} ${data.units || ''}`.toLowerCase();
      return periodText.includes(searchInput.toLowerCase());
    });
    setDisplayedData(filteredData);
  }, [searchInput, ratesData]);

  // Update points conversion and currency when selected country changes and countries are loaded
  useEffect(() => {
    if (countries.length > 0 && selectedCountry) {
      const selectedCountryData = countries.find(c => c.iso === selectedCountry);
      if (selectedCountryData) {
        setPointsConversion(selectedCountryData.pointsConversion);
        setCurrency(selectedCountryData.currency);
      }
    }
  }, [selectedCountry, countries]);

  const toggleDrawerHandler = (drawer, data = {}) => {
    handleClose();
    drawerCtx.setDetails(drawer, { type: drawer, ...data });
    drawerCtx.openDrawer();
  };

  useEffect(() => {
    if (drawerCtx.pageUpdate?.page === "PricingTable") {
      getAllRates(selectedCountry);
      drawerCtx.setPageUpdate({ page: "" });
    }
  }, [drawerCtx.pageUpdate]);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div className="pricing-table-container">
        <div className='header-section'>
          <span className='title'>PRICING TABLE</span>
          <div className='header-items-div'>
            <FormControl className="country-select" size="small">
              <Select
                value={selectedCountry}
                onChange={handleCountryChange}
                sx={{
                  height: '38px',
                  minWidth: '200px',
                  backgroundColor: '#f1f2f6',
                  borderRadius: '4px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                {countries.map((country) => (
                  <MenuItem key={country.iso} value={country.iso}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {pointsConversion !== null && currency && (
              <div className="points-conversion">
                1 Point = {formatNumber(pointsConversion)} {currency}
              </div>
            )}
            <PageSearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by period (e.g., 1 month)" />
          </div>
        </div>

        <TableContainer component={Paper} className='listing-table pricing-table'>
          <Table sx={{ minWidth: 500 }} stickyHeader>
            <TableHead>
              <TableRow className='table-head'>
                <TableCell align="left" className='head-cell pricing-head-cell'>Period</TableCell>
                <TableCell align="left" className='head-cell pricing-head-cell'>Price</TableCell>
                <TableCell align="left" className='head-cell pricing-head-cell'>Agent Fee</TableCell>
                <TableCell align="right" className='head-cell pricing-head-cell'></TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={loading ? "data-loading-circle" : "pricing-table-body"}>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ border: "0px", width: "100%", paddingTop: "100px" }}>
                    <CircularProgress size={52} style={{ color: "#2b1de8", border: "0px" }} />
                  </TableCell>
                </TableRow>
              ) : (
                (rowsPerPage > 0 ? displayedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : displayedData).map((data, index) => (
                  <TableRow 
                    key={data.id} 
                    className={`pricing-table-row ${updatingRows.has(data.id) ? "updating-row" : ""}`} 
                    style={{ backgroundColor: index % 2 !== 0 ? '#fafafc' : 'white' }}
                  >
                    <TableCell className='table-cell pricing-table-cell' align="left">{formatPeriodDisplay(data.amount, data.units)}</TableCell>
                    <TableCell className='table-cell pricing-table-cell' align="left">{formatNumber(data.price)}</TableCell>
                    <TableCell className='table-cell pricing-table-cell' align="left">{formatNumber(data.agentFee)}</TableCell>
                    <TableCell className='table-cell pricing-table-cell' style={{ width: '80px' }} align="left">
                      <div className='icons-container'>
                        <MoreVertRounded 
                          aria-controls={open ? 'fade-menu' : undefined} 
                          aria-haspopup="true" 
                          aria-expanded={open ? 'true' : undefined} 
                          onClick={(e) => {
                            setSelectedRates(data);
                            handleClick(e);
                          }} 
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={4} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 500 }}>
            <TableBody>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={3}
                  count={displayedData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page',
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Menu id="fade-menu" MenuListProps={{ 'aria-labelledby': 'fade-button' }} anchorEl={anchorEl} open={open} onClose={handleClose} TransitionComponent={Fade}>
        <MenuItem onClick={() => toggleDrawerHandler('edit-pricing', { ...selectedRates, country: selectedCountry })}> 
          <EditRounded style={{ marginRight: '8px', fontSize: '18px' }} /> 
          Edit Rates 
        </MenuItem>
      </Menu>
    </>
  );
}

